import {HoistModel, lookup, PlainObject, XH} from '@xh/hoist/core';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {action, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {mergeDeep} from '@xh/hoist/utils/js';
import {cloneDeep, random, range} from 'lodash';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';
import {FullQuote} from '../../../../data/DataTypes';

/** Days-to-expiry for each surface column - mimics a typical weekly/monthly/quarterly/LEAPS chain. */
const EXPIRY_DAY_OFFSETS = [30, 59, 94, 184, 276, 366, 456, 639, 821, 1003, 1368, 1733];
const STRIKE_COUNT = 15;
const STRIKE_SPREAD = 0.5;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_SPOT = 100;

export class VolSurfaceWidgetModel extends HoistModel {
    @observable accessor symbol: string;
    @observableRef accessor layout: PlainObject = this.createLayout();
    @observableRef accessor data;
    @observableRef accessor plotConfig = this.createConfig();
    @lookup(DashViewModel) dashViewModel: DashViewModel;

    constructor() {
        super();

        this.addReaction({
            track: () => XH.darkTheme,
            run: () => this.setTheme(),
            fireImmediately: true
        });
    }

    override onLinked() {
        this.markPersist('symbol', {dashViewModel: this.dashViewModel});

        this.dashViewModel.extraMenuItems.push({
            icon: Icon.gear(),
            text: 'Configure',
            items: ['SPX', 'VIX', 'IXIC', 'DJI', 'RUT', 'OSX', 'XAU', 'SOX'].map(symbol => ({
                text: symbol,
                key: symbol,
                actionFn: () => this.setSymbol(symbol)
            }))
        });
    }

    private get colorScheme(): ColorScheme {
        const isDark = XH.darkTheme;
        return {
            bgColor: isDark ? '#151e23' : '#f5f5f5',
            activeColor: isDark ? '#ffffff' : '#263238',
            mutedColor: '#757575'
        };
    }

    override async doLoadAsync() {
        const {symbol} = this;
        if (!symbol) return;

        const spot = await this.getSpotPriceAsync(symbol);
        const data = [this.buildSurfaceTrace(spot)];

        runInAction(() => (this.data = data));
    }

    /**
     * Builds a smooth, analytic vol surface trace centered on the given spot price. Values are
     * driven by a per-load randomized term-structure + smile shape rather than independent
     * per-cell noise, so the resulting surface renders smoothly instead of choppy.
     */
    private buildSurfaceTrace(spot: number): PlainObject {
        const x = EXPIRY_DAY_OFFSETS.map(days => new Date(Date.now() + days * MS_PER_DAY)),
            y = this.buildStrikes(spot),
            z = this.buildZ(y, spot);

        return {
            x,
            y,
            z,
            type: 'surface',
            connectgaps: true,
            showscale: false,
            colorbar: {tickfont: {color: this.colorScheme.activeColor}},
            contours: {
                x: {
                    highlight: true,
                    highlightwidth: 4,
                    highlightcolor: this.colorScheme.activeColor
                },
                y: {
                    highlight: false
                },
                z: {highlight: false}
            },
            hoverinfo: 'none'
        };
    }

    /** Strikes spanning +/- STRIKE_SPREAD around the spot price, rounded to sensible increments. */
    private buildStrikes(spot: number): number[] {
        return range(STRIKE_COUNT).map(i => {
            const pct = -STRIKE_SPREAD + (2 * STRIKE_SPREAD * i) / (STRIKE_COUNT - 1);
            return this.roundStrike(spot * (1 + pct), spot);
        });
    }

    private roundStrike(value: number, spot: number): number {
        const increment = spot >= 10000 ? 100 : spot >= 1000 ? 25 : spot >= 100 ? 5 : spot >= 10 ? 1 : 0.5;
        return Math.round(value / increment) * increment;
    }

    /**
     * Generates a smooth implied-vol surface as an analytic function of strike and expiry - a
     * term structure (short vs. long-dated vol, decaying over time) combined with a smile/skew
     * in log-moneyness. Shape parameters are randomized once per surface (not per cell), so the
     * surface stays smooth while still varying across tickers and reloads.
     */
    private buildZ(strikes: number[], spot: number): number[][] {
        const shortVol = random(0.35, 0.65, true),
            longVol = random(0.15, 0.3, true),
            decayYears = random(0.15, 0.35, true),
            skew = random(-0.25, -0.1, true),
            curvature = random(0.08, 0.2, true);

        return strikes.map(strike => {
            const logMoneyness = Math.log(strike / spot);
            return EXPIRY_DAY_OFFSETS.map(days => {
                const tau = days / 365,
                    term = longVol + (shortVol - longVol) * Math.exp(-tau / decayYears),
                    smile = skew * logMoneyness + curvature * logMoneyness * logMoneyness;
                return Math.max(term + smile, 0.05);
            });
        });
    }

    /**
     * Configured symbols here (SPX, VIX, etc.) are broad indices, which the security master
     * stores with a leading caret (e.g. '^SPX') - see SecMaster. A plain-ticker lookup 404s for
     * every one of them, which is what previously made every surface fall back to DEFAULT_SPOT
     * regardless of symbol.
     */
    private async getSpotPriceAsync(symbol: string): Promise<number> {
        try {
            const quote: FullQuote = await XH.fetchService.getJson({
                url: `${XH.getConf('svcApiUrl')}/quote/fullquote/^${symbol}`,
                ...getDefaultOptions()
            });
            const price = (quote?.bid + quote?.ask) / 2;
            return price > 0 ? price : DEFAULT_SPOT;
        } catch (e) {
            return DEFAULT_SPOT;
        }
    }

    @action
    private setSymbol(symbol: string) {
        this.symbol = symbol;
        this.dashViewModel.title = symbol;
        this.loadAsync();
    }

    @action
    private setTheme() {
        let colorScheme = this.colorScheme;

        if (this.data) {
            this.data[0].colorbar.tickfont.color = colorScheme.activeColor;
            this.data[0].contours.y.highlightcolor = colorScheme.activeColor;
            this.data = [this.data[0]];
        }

        this.updateLayout({
            paper_bgcolor: colorScheme.bgColor,
            plot_bgcolor: colorScheme.bgColor,
            title: {font: {color: colorScheme.activeColor}},
            scene: {
                xaxis: {color: colorScheme.activeColor},
                yaxis: {color: colorScheme.activeColor},
                zaxis: {color: colorScheme.activeColor}
            }
        });
    }

    @action resize(r: DOMRect) {
        this.data = cloneDeep(this.data);
    }

    @action
    private updateLayout(update?: any) {
        this.layout = mergeDeep(cloneDeep(this.layout), update);
    }

    private createLayout(): PlainObject {
        let colorScheme = this.colorScheme;
        return {
            autosize: true,
            xaxis: {color: colorScheme.bgColor},
            yaxis: {color: colorScheme.bgColor},
            scene: {
                xaxis: {title: {text: 'Expiries'}, showspikes: false},
                yaxis: {title: {text: 'strikes'}, showspikes: false},
                zaxis: {title: {text: 'Vol (%)'}, showspikes: false, tickformat: '.2%'},
                camera: {
                    center: {x: 0, y: 0, z: -0.3},
                    up: {x: 0, y: 0, z: 1},
                    eye: {x: 1.5, y: -1, z: 1.5}
                }
            },
            margin: {
                l: 10,
                r: 10,
                b: 5,
                t: 10
            }
        };
    }

    private createConfig(): PlainObject {
        return {displayModeBar: false};
    }
}

export type ColorScheme = {
    bgColor: string;
    activeColor: string;
    mutedColor: string;
};
